import * as types from '../mutation-types';
import CustomerNotificationsAPI from '../../api/customerNotifications';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const initialFilters = {
  from: null,
  to: null,
  category: null,
  sentByService: null,
  search: '',
  read: 'any',
  converted: 'any',
};

const state = {
  records: [],
  detail: null,
  lastSendResult: null,
  filters: { ...initialFilters },
  meta: {
    hasMore: false,
    nextBeforeCreatedAt: null,
    totalUnread: 0,
  },
  uiFlags: {
    fetchingList: false,
    fetchingDetail: false,
    appending: false,
    sending: false,
  },
};

const getters = {
  getCustomerNotifications: _state => _state.records,
  getCustomerNotificationDetail: _state => _state.detail,
  getCustomerNotificationFilters: _state => _state.filters,
  getCustomerNotificationMeta: _state => _state.meta,
  getCustomerNotificationUIFlags: _state => _state.uiFlags,
  getCustomerNotificationSendResult: _state => _state.lastSendResult,
};

const buildParams = filters => {
  const params = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.category) params.category = filters.category;
  if (filters.sentByService) params.sent_by_service = filters.sentByService;
  if (filters.search) params.search = filters.search;
  if (filters.read && filters.read !== 'any') params.read = filters.read;
  if (filters.converted && filters.converted !== 'any') {
    params.converted = filters.converted;
  }
  return params;
};

const actions = {
  setFilter({ commit }, payload) {
    commit(types.default.SET_CUSTOMER_NOTIFICATIONS_FILTERS, payload);
  },

  resetFilters({ commit }) {
    commit(types.default.SET_CUSTOMER_NOTIFICATIONS_FILTERS, { ...initialFilters });
  },

  async fetchList({ commit, state: _state }) {
    commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { fetchingList: true });
    try {
      const params = buildParams(_state.filters);
      const response = await CustomerNotificationsAPI.list(params);
      const { items, has_more, next_before_created_at, total_unread } = response.data;
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS, items);
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_META, {
        hasMore: has_more,
        nextBeforeCreatedAt: next_before_created_at,
        totalUnread: total_unread,
      });
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { fetchingList: false });
      return items;
    } catch (error) {
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { fetchingList: false });
      return throwErrorMessage(error);
    }
  },

  async fetchMore({ commit, state: _state }) {
    if (!_state.meta.hasMore || !_state.meta.nextBeforeCreatedAt) return;

    commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { appending: true });
    try {
      const params = { ...buildParams(_state.filters), before_created_at: _state.meta.nextBeforeCreatedAt };
      const response = await CustomerNotificationsAPI.list(params);
      const { items, has_more, next_before_created_at } = response.data;
      commit(types.default.APPEND_CUSTOMER_NOTIFICATIONS, items);
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_META, {
        hasMore: has_more,
        nextBeforeCreatedAt: next_before_created_at,
      });
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { appending: false });
    } catch (error) {
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { appending: false });
      return throwErrorMessage(error);
    }
    return null;
  },

  async fetchDetail({ commit }, id) {
    commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { fetchingDetail: true });
    try {
      const response = await CustomerNotificationsAPI.fetchOne(id);
      commit(types.default.SET_CUSTOMER_NOTIFICATION_DETAIL, response.data);
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { fetchingDetail: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { fetchingDetail: false });
      return throwErrorMessage(error);
    }
  },

  clearDetail({ commit }) {
    commit(types.default.SET_CUSTOMER_NOTIFICATION_DETAIL, null);
  },

  async sendNotification({ commit, dispatch }, payload) {
    commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { sending: true });
    try {
      const response = await CustomerNotificationsAPI.send(payload);
      commit(types.default.SET_CUSTOMER_NOTIFICATION_SEND_RESULT, response.data);
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { sending: false });
      // Refresh list so newly created notifications appear at the top
      dispatch('fetchList');
      return response.data;
    } catch (error) {
      commit(types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG, { sending: false });
      return throwErrorMessage(error);
    }
  },

  clearSendResult({ commit }) {
    commit(types.default.SET_CUSTOMER_NOTIFICATION_SEND_RESULT, null);
  },
};

const mutations = {
  [types.default.SET_CUSTOMER_NOTIFICATIONS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.default.SET_CUSTOMER_NOTIFICATIONS](_state, items) {
    _state.records = items;
  },
  [types.default.APPEND_CUSTOMER_NOTIFICATIONS](_state, items) {
    _state.records = [..._state.records, ...items];
  },
  [types.default.SET_CUSTOMER_NOTIFICATIONS_META](_state, data) {
    _state.meta = { ..._state.meta, ...data };
  },
  [types.default.SET_CUSTOMER_NOTIFICATIONS_FILTERS](_state, data) {
    _state.filters = { ..._state.filters, ...data };
  },
  [types.default.SET_CUSTOMER_NOTIFICATION_DETAIL](_state, data) {
    _state.detail = data;
  },
  [types.default.SET_CUSTOMER_NOTIFICATION_SEND_RESULT](_state, data) {
    _state.lastSendResult = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
